import { useEffect } from 'react';
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type ListSupportTicketsResponse,
  type ShowSupportTicketResponse,
  type SupportTicketAttachment,
  showSupportTicketContract,
  supportAttachmentDownloadPath
} from '@bliks-io/contracts';
import { apiClient } from '../client';
import { supportTicketKeys } from '../query-keys';

// Support answers by mail through Zammad, which cannot push to us; while the
// ticket is on screen, a new message or status shows up within this interval.
const OPEN_TICKET_REFRESH_MS = 15_000;

export const supportTicketQueryOptions = (ticketId: number) =>
  queryOptions({
    queryKey: supportTicketKeys.detail(ticketId),
    refetchInterval: OPEN_TICKET_REFRESH_MS,
    refetchIntervalInBackground: false,
    queryFn: async ({ signal }): Promise<ShowSupportTicketResponse> => {
      const { data } = await apiClient.get<ShowSupportTicketResponse>(
        showSupportTicketContract.path.replace(':ticketId', String(ticketId)),
        { signal }
      );
      return data;
    }
  });

export function useSupportTicket(ticketId: number) {
  const queryClient = useQueryClient();
  const query = useQuery(supportTicketQueryOptions(ticketId));
  const loadedAt = query.dataUpdatedAt;

  useEffect(
    function clearNewReplyMark() {
      // The backend counts the ticket as seen once it served it; the cached
      // list and the sidebar badge follow now rather than at their next poll.
      if (!loadedAt) return;
      queryClient.setQueryData<ListSupportTicketsResponse>(supportTicketKeys.lists(), (cached) =>
        cached?.tickets.some((ticket) => ticket.id === ticketId && ticket.hasNewReply)
          ? {
              tickets: cached.tickets.map((ticket) =>
                ticket.id === ticketId ? { ...ticket, hasNewReply: false } : ticket
              )
            }
          : cached
      );
    },
    [loadedAt, ticketId, queryClient]
  );

  return query;
}

export async function fetchSupportAttachment(
  params: { ticketId: number; messageId: number; attachmentId: number },
  signal?: AbortSignal
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(supportAttachmentDownloadPath(params), {
    responseType: 'blob',
    signal
  });
  return data;
}

/**
 * The endpoint always answers octet-stream (see the download controller), so
 * the bytes are re-typed here with the type Zammad declared; only callers that
 * already checked the type is a safe raster image should render the result.
 */
export function useSupportAttachmentImage(
  ticketId: number,
  messageId: number,
  attachment: SupportTicketAttachment | null
) {
  return useQuery({
    queryKey: supportTicketKeys.attachment(ticketId, messageId, attachment?.id ?? 0),
    queryFn: async ({ signal }) => {
      const data = await fetchSupportAttachment(
        { ticketId, messageId, attachmentId: attachment?.id ?? 0 },
        signal
      );
      return new Blob([data], { type: attachment?.mimeType });
    },
    enabled: attachment !== null,
    staleTime: Infinity
  });
}
